import { generatePkceData } from './pkce';

const ClientId = '5d642353-3cfc-480f-a051-700d8b835ea8';
const AuthorizeEndpoint = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const TokenEndpoint = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
const Scope = 'api://api.customerportal.avonova.se/.default';

// Customer SSO (OidcClient) stores its state under 'oidc.' and also returns to
// /login — the differing prefix is what tells the two callbacks apart.
const StorageKeyPrefix = 'entra.';

export default class EntraOidcClient {
  constructor(private readonly state?: State) {}

  async authorizeRedirect() {
    removeStaleStates();

    const [codeVerifier, codeChallenge] = await generatePkceData();
    const redirectUri = `${window.location.origin}/login`;

    const state = crypto.randomUUID();
    const nonce = crypto.randomUUID();

    const stateObj: State = {
      codeVerifier,
      nonce,
      clientId: ClientId,
      redirectUri,
    };

    sessionStorage.setItem(`${StorageKeyPrefix}${state}`, JSON.stringify(stateObj));

    const params = new URLSearchParams();
    params.set('client_id', ClientId);
    params.set('redirect_uri', redirectUri);
    params.set('scope', Scope);
    params.set('response_type', 'code');
    params.set('code_challenge', codeChallenge);
    params.set('code_challenge_method', 'S256');
    params.set('state', state);
    params.set('nonce', nonce);
    params.set('prompt', 'select_account');

    window.location.replace(`${AuthorizeEndpoint}?${params.toString()}`);
  }

  async getToken(code: string): Promise<TokenResponse> {
    if (!this.state) return {};

    try {
      const params = new URLSearchParams();

      params.append('grant_type', 'authorization_code');
      params.append('code', code);
      params.append('code_verifier', this.state.codeVerifier);
      params.append('client_id', ClientId);
      params.append('redirect_uri', this.state.redirectUri);
      params.append('scope', Scope);

      const response = await fetch(TokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.status === 400) {
        return await response.json();
      }

      if (response.status === 401) {
        return {};
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch access token: ${response.status} ${response.statusText}`);
      }

      const tokenResponse: TokenResponse = await response.json();
      if (!tokenResponse.access_token) return {};

      return tokenResponse;
    } catch (error) {
      console.error(error);
      return {};
    }
  }

  static hasState(state: string): boolean {
    return !!sessionStorage.getItem(`${StorageKeyPrefix}${state}`);
  }

  static fromState(state: string): EntraOidcClient | undefined {
    const storageKey = `${StorageKeyPrefix}${state}`;
    const stateJson = sessionStorage.getItem(storageKey);
    if (!stateJson) return;

    const stateObj: State = JSON.parse(stateJson);

    // Remove storage item to avoid reusing authentication flow
    sessionStorage.removeItem(storageKey);

    return new EntraOidcClient(stateObj);
  }
}

type State = {
  codeVerifier: string;
  nonce: string;
  clientId: string;
  redirectUri: string;
};

type TokenResponse = {
  error?: string;
  error_description?: string;
  access_token?: string;
};

function removeStaleStates() {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(StorageKeyPrefix)) {
      sessionStorage.removeItem(key);
    }
  }
}
